import { useMemo } from "react"

import cls from "classnames"

import { usePhoneCountries } from "../../hooks/usePhoneCountries"
import { browserCountry, readPhone, toPhoneValue } from "../../Utils/phone/readPhone"
import InputComponent from "../Input"
import CountrySelect from "./components/CountrySelect"

import "./styles.less"

const dialChars = (s = "") => s.replace(/[^\d+]/g, "")

// Value: { code, countryCode, name, id, number, e164, valid }. `number` is the text
// the input shows; save `e164`, and only when `valid` is true.
const PhoneInput = ({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder = "Enter phone number",
  disabled,
  ...props
}) => {
  const { countries } = usePhoneCountries()
  const text = value?.number || ""

  const defaultIso = useMemo(
    () => browserCountry(countries.map((c) => c.country_code)),
    [countries]
  )
  const iso = value?.countryCode || defaultIso

  const emit = (result) => onChange?.(toPhoneValue(countries, result))

  const handlePhoneChange = (e) => {
    let next = e.target.value
    // Backspace over a formatting character ("(", ")", "-", " ") changes no digit,
    // and re-formatting would put it straight back. Drop the digit before it instead.
    if (next.length < text.length && dialChars(next) === dialChars(text)) {
      const caret = e.target.selectionStart ?? next.length
      next = next.slice(0, caret).replace(/\d(?=\D*$)/, "") + next.slice(caret)
    }
    emit(readPhone(next, iso))
  }

  const handleCountryChange = (nextIso) => emit(readPhone(text, nextIso))

  return (
    <InputComponent
      name="phone"
      inputMode="tel"
      autoComplete="tel"
      className={cls("phoneInput")}
      disabled={disabled}
      addonBefore={
        <CountrySelect
          countries={countries}
          value={iso}
          disabled={disabled}
          onChange={handleCountryChange}
        />
      }
      value={text}
      onChange={handlePhoneChange}
      onFocus={onFocus}
      placeholder={placeholder}
      onBlur={onBlur}
      {...props}
    />
  )
}

export default PhoneInput
